'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '@/features/auth'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/features/notifications/api/notifications.api'
import { queryKeys } from '@/lib/queryKeys'

/**
 * The real endpoint auto-scopes to the caller from the auth token, so there's no
 * audience/recipientId to pass. The unread badge needs a scope-wide count, not just what's
 * on the dropdown's own page — fetched via a second, minimal `pageSize: 1, unreadOnly: true`
 * query, reading only its `.total` (same pattern as `useCalls({ page: 1, pageSize: 1 })` for
 * the "Total Calls" stat card in `admin-staff-view.tsx`).
 */
export function useNotifications() {
  const authedFetch = useApiFetch()

  const listQuery = useQuery({
    queryKey: queryKeys.notifications.list({ page: 1, pageSize: 20 }),
    queryFn: () => fetchNotifications(authedFetch, { page: 1, pageSize: 20 }),
  })
  const unreadQuery = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: () => fetchNotifications(authedFetch, { page: 1, pageSize: 1, unreadOnly: true }),
  })

  return {
    notifications: listQuery.data?.items ?? [],
    unreadCount: unreadQuery.data?.total ?? 0,
    isLoading: listQuery.isLoading,
  }
}

export function useMarkNotificationRead() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(authedFetch, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => markAllNotificationsRead(authedFetch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
